import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { getLanguageCode } from './user';
import languageCodeMappings from '~shared/locales/languageCodeMappings';
import { DEFAULT_LANG } from './constants';

/**
 * We use an Internal three letter language code. i18next uses 2 letter codes internally.
 */
class I18n {
    constructor() {
        this.defaultSpectateLanguage = DEFAULT_LANG; // 3 letters - must exist in languageCodeMappings
        this.i18next = i18next;
        this.t = i18next.t.bind(i18next);
        this.loadPlugins();
    }

    loadPlugins() {
        this.i18next.use(initReactI18next);
    }

    /**
     * Get the language from bbClientState.
     * @returns {string} the language
     */
    getLanguage() {
        let lang = getLanguageCode();

        if (isEmpty(lang)) {
            lang = this.defaultSpectateLanguage;
        }

        return lang;
    }

    /**
     * Translate given language code to 2 letter code
     *
     * @param  {String} lang    language code
     * @return {String}         2 letter code.
     */
    translateLangCodeMapping(lang) {
        if (!isEmpty(languageCodeMappings[lang])) {
            return languageCodeMappings[lang];
        }

        return languageCodeMappings[this.defaultSpectateLanguage];
    }

    async getResources(locale) {
        let locales = [this.defaultSpectateLanguage];
        if (locale !== this.defaultSpectateLanguage) {
            locales.push(locale);
        }

        let resources = {};
        await Promise.all(
            locales.map(async (locale) => {
                const { default: translation } = await import(
                    /* webpackChunkName: "locales/[request]" */
                    `../locales/${locale}`
                );
                resources[this.translateLangCodeMapping(locale)] = { translation };
            })
        ).catch(e => console.error(e)); //eslint-disable-line

        return resources;
    }

    async initialise() {
        const spectateLang = this.getLanguage();
        const lng = this.translateLangCodeMapping(spectateLang);
        const fallbackLng = this.translateLangCodeMapping(this.defaultSpectateLanguage);
        const resources = await this.getResources(spectateLang);

        await this.i18next.init({
            lng,
            fallbackLng,
            returnEmptyString: false,
            debug: process.env.NODE_ENV === 'development',
            resources,
        });
    }
}

export default new I18n();
